import { useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import type { CenterstageVideo } from '../../api/centerstageApi';
import { AdminStatusMessage } from '../../admin-ui';
import { CENTERSTAGE_COPY as COPY } from './centerstageCopy';
import type { CenterstageStyles } from './centerstageStyles';

export interface CenterstageVideoGridProps {
  styles: CenterstageStyles;
  status: 'pending' | 'error' | 'success';
  videos: readonly CenterstageVideo[];
  /** The library's total for the current filters, once the first page has loaded. */
  total: number | null;
  hasMore: boolean;
  loadingMore: boolean;
  /** The last "Show more" failed; the loaded cards stay. */
  loadMoreFailed: boolean;
  onShowMore: () => void;
  onRetry: () => void;
  /** Opens a video from the page's card. */
  onOpen?: (video: CenterstageVideo) => void;
  /** Draws each card instead of the page's card (the Author with AI picker's variant). */
  renderCard?: (video: CenterstageVideo) => ReactNode;
  /** The announced result count above the cards; the picker leaves it out. */
  showCount?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
}

/** The result count (announced), then the cards with "Show more", or the loading, error or empty panel. */
export function CenterstageVideoGrid({
  styles,
  status,
  videos,
  total,
  hasMore,
  loadingMore,
  loadMoreFailed,
  onShowMore,
  onRetry,
  onOpen,
  renderCard,
  showCount = true,
  emptyTitle = COPY.emptyTitle,
  emptyBody = COPY.emptyBody,
}: CenterstageVideoGridProps) {
  const countText = status === 'success' && total !== null ? COPY.resultCount(total) : '';

  return (
    <section aria-label={COPY.title} style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
      {showCount ? <p role="status" aria-live="polite" style={styles.resultCount}>{countText}</p> : null}
      {status === 'pending' ? (
        <div style={styles.loadingPanel}>{COPY.loading}</div>
      ) : status === 'error' ? (
        <div style={styles.errorPanel}>
          <AdminStatusMessage tone="error">{COPY.loadError}</AdminStatusMessage>
          <button type="button" onClick={onRetry} style={styles.secondaryButton}>{COPY.retry}</button>
        </div>
      ) : videos.length === 0 ? (
        <div style={styles.emptyPanel}>
          <p style={styles.emptyTitle}>{emptyTitle}</p>
          <p style={styles.emptyBody}>{emptyBody}</p>
        </div>
      ) : (
        <>
          <ul style={styles.grid}>
            {videos.map((video) => (
              <li key={video.id} style={{ minWidth: 0 }}>
                {renderCard ? renderCard(video) : <VideoCard styles={styles} video={video} onOpen={(open) => onOpen?.(open)} />}
              </li>
            ))}
          </ul>
          {loadMoreFailed ? <AdminStatusMessage tone="error">{COPY.loadError}</AdminStatusMessage> : null}
          {hasMore ? (
            <div style={styles.showMoreRow}>
              <button
                type="button"
                onClick={onShowMore}
                disabled={loadingMore}
                style={{ ...styles.secondaryButton, ...(loadingMore ? styles.buttonDisabled : {}) }}
              >
                {loadingMore ? COPY.loadingMore : COPY.showMore}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/** A card's 16:9 still, or a neutral tile with a play glyph when the video has none. `children` overlay the still. */
export function CenterstagePoster({ styles, video, children }: { styles: CenterstageStyles; video: CenterstageVideo; children?: ReactNode }) {
  const poster = video.posterUrl ?? video.thumbnailUrl;
  return (
    <span style={styles.poster}>
      {poster ? (
        <img src={poster} alt="" loading="lazy" style={styles.posterImage} />
      ) : (
        <span style={styles.posterFallback}>
          <FontAwesomeIcon icon={faPlay} aria-hidden="true" />
        </span>
      )}
      {children}
    </span>
  );
}

function VideoCard({ styles, video, onOpen }: { styles: CenterstageStyles; video: CenterstageVideo; onOpen: (video: CenterstageVideo) => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      // Buttons take phrasing content only, so the card text is spans; the title names the card.
      aria-label={video.title}
      onClick={() => onOpen(video)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...styles.card, ...(hovered ? styles.cardHover : {}) }}
    >
      <CenterstagePoster styles={styles} video={video} />
      <span style={styles.cardBody}>
        <span style={styles.cardTitle}>{video.title}</span>
        {video.description ? <span style={styles.cardDescription}>{video.description}</span> : null}
      </span>
    </button>
  );
}
